import { useState } from 'react';
import type { FormEvent } from 'react';
import { MAX_SWITCH_REASON_LENGTH } from '@nadoutong/contracts';
import type { RoutingSwitchEventRecord } from '@nadoutong/contracts';
import { OpsApiError } from '../../api/client.ts';
import { SWITCH_EVENTS_PAGE_SIZE } from '../../api/routingDr.ts';
import { useRollbackRoutingSwitch, useRoutingSwitchEvents } from '../../api/useRoutingDr.ts';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import { formatLocalTimestamp } from '../../components/formatters.ts';
import { useT } from '../../i18n/index.ts';
import { OverrideInForceNotice } from './OverrideInForceNotice.tsx';
import {
  Button,
  Chip,
  Dialog,
  EmptyState,
  FormField,
  Panel,
  TBody,
  Table,
  Td,
  Textarea,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';
import {
  ROUTING_CONVERGENCE_WORST_CASE_SECONDS,
  convergenceOutcome,
  convergenceWindowSeconds,
  eventConvergenceOutcome,
} from './convergence.ts';

/**
 * Recent global switches, and the rollback that replays the latest one
 * backwards (#1421 P3b).
 *
 * Only the newest event offers a rollback. The endpoint always rolls back the
 * *latest* switch — it is a ledger, not an undo stack — so offering the action
 * on an older row would promise a target the gateway would not use.
 *
 * ── Why the dialog re-reads the head ─────────────────────────────────────
 * The request pins `expected_event_id`, so a rollback can no longer land on a
 * target the operator never saw: a superseded pin is refused by the gateway.
 * But a 409 is a worse experience than never showing a stale target in the
 * first place, and this list is cached with a 30s stale time and no
 * focus refetch, so the row behind the button can easily be a minute old. The
 * dialog therefore refetches on open, keeps its submit disabled until that
 * settles, and — if the head moved — re-states the new target and makes the
 * operator confirm it again rather than submitting a draft they formed
 * against different facts. The same re-confirmation runs after a `HEAD_MOVED`
 * refusal, so the error copy's "re-read, do not retry" is what the dialog
 * actually enforces instead of merely advising.
 */

interface RollbackDraft {
  /** The event this rollback reverses; sent as `expected_event_id`. */
  readonly event_id: string;
  readonly from_config_id: string;
  readonly to_config_id: string;
  readonly reason: string;
  /**
   * True when the head moved under an open dialog. Blocks submission until
   * the operator acknowledges the target they are now looking at.
   */
  readonly needs_reconfirm: boolean;
  /**
   * True when the re-read itself failed. Distinct from `needs_reconfirm`
   * because there is nothing to confirm: the dialog cannot say what the head
   * is, so the honest state is "unknown", and no acknowledgement the operator
   * could give would make the displayed target trustworthy.
   */
  readonly reread_failed: boolean;
}

/**
 * The newest `global` event with somewhere to roll back to.
 *
 * **Scans**, matching the gateway's `latestGlobalSwitchEvent`, which filters
 * `WHERE kind = 'global'` rather than inspecting only the head. This used to
 * test `events[0].kind === 'global'` and withdraw the button otherwise — so a
 * single engage/disengage pair (#1422) left two `override` rows on top of the
 * log and removed rollback permanently, until somebody performed another
 * global switch, while the endpoint itself would have succeeded immediately.
 *
 * That is precisely the window where rollback is most wanted: an override
 * incident has just ended and the global switch that preceded it needs
 * undoing. The console must agree with the endpoint about what the rollback
 * target is, because it is also the thing that pins `expected_event_id`.
 */
function rollbackableHead(
  events: readonly RoutingSwitchEventRecord[],
): RoutingSwitchEventRecord | null {
  // The list arrives newest-first, so the first match is the newest.
  return events.find(
    (event) => event.kind === 'global' && event.from_config_id !== null,
  ) ?? null;
}

function draftFromHead(
  head: RoutingSwitchEventRecord,
  reason: string,
  needsReconfirm: boolean,
): RollbackDraft {
  return {
    event_id: head.id,
    from_config_id: head.from_config_id ?? '',
    to_config_id: head.to_config_id ?? '',
    reason,
    needs_reconfirm: needsReconfirm,
    reread_failed: false,
  };
}

export function RoutingSwitchEventsPanel(): JSX.Element {
  const t = useT();
  const toast = useToast();
  const events = useRoutingSwitchEvents();
  const rollback = useRollbackRoutingSwitch();
  const [draft, setDraft] = useState<RollbackDraft | null>(null);
  const [syncingHead, setSyncingHead] = useState(false);

  const rows = events.data?.events ?? [];
  const rollbackTarget = rollbackableHead(rows);

  /**
   * Re-reads the head and folds it into the open draft.
   *
   * Three outcomes, all of which must be distinguishable to the operator:
   *
   *  - **the re-read failed** — the dialog keeps whatever it was showing, says
   *    so, and stays un-submittable. Arming submit here would make the
   *    dialog's whole premise ("this target was just re-read") false with no
   *    signal, and closing would drop the typed reason for what may be a
   *    transient network error.
   *  - **the log no longer offers a rollback** — the head became an
   *    `override`, or its predecessor is gone. There is genuinely nothing to
   *    confirm, so the dialog closes, but it says why: a dialog that vanishes
   *    mid-incident with no explanation is indistinguishable from a bug.
   *  - **a head came back** — fold it in, and demand re-confirmation when it
   *    is not the event the draft was pinned to, or when the caller already
   *    knows the pin is dead.
   *
   * `forceReconfirm` exists for the `HEAD_MOVED` path. There the gateway has
   * already asserted, authoritatively and under a lock, that the pinned event
   * is not the head; if this re-read disagrees — replica lag, a cached
   * `GET /switch-events`, the other write not yet visible — an id comparison
   * would find "no change", drop the banner and re-arm submit on the same
   * rejected pin, inviting exactly the retry the error copy forbids. A 409 is
   * sufficient evidence on its own.
   */
  async function syncHeadIntoDraft(reason: string, forceReconfirm = false): Promise<void> {
    setSyncingHead(true);
    try {
      const fresh = await events.refetch();
      if (fresh.isError) {
        setDraft((current) => current === null ? current : {
          ...current,
          reason: reason.length > 0 ? reason : current.reason,
          reread_failed: true,
        });
        return;
      }
      const head = rollbackableHead(fresh.data?.events ?? []);
      if (head === null) {
        setDraft(null);
        toast.info(t('routing_dr.events.toast.rollback_unavailable'));
        return;
      }
      setDraft((current) => {
        if (current === null) return current;
        const moved = forceReconfirm || current.event_id !== head.id;
        // The operator's typed reason survives the re-read — the head moving
        // does not make their words wrong, and retyping an incident reason is
        // exactly the friction that produces "see above".
        return draftFromHead(head, reason.length > 0 ? reason : current.reason, moved);
      });
    } finally {
      setSyncingHead(false);
    }
  }

  function openRollback(head: RoutingSwitchEventRecord): void {
    setDraft(draftFromHead(head, '', false));
    void syncHeadIntoDraft('');
  }

  /**
   * `409 ROUTING_SWITCH_HEAD_MOVED` is not a retryable failure and must not
   * read like one. It means somebody else switched the global config while
   * this panel was open, so the rollback the operator asked for would undo
   * *their* switch too. The right next step is to re-read the log — which is
   * why that path refetches it *and* re-arms the dialog's confirmation,
   * instead of updating the table behind a dialog that still shows the stale
   * target with a live submit button.
   */
  function rollbackErrorMessage(error: unknown, reason: string): string {
    if (error instanceof OpsApiError) {
      if (error.code === 'ROUTING_SWITCH_HEAD_MOVED') {
        return t('routing_dr.events.error.head_moved');
      }
      if (error.code === 'ROUTING_SWITCH_NO_ROLLBACK_TARGET') {
        return t('routing_dr.events.error.no_target');
      }
      if (error.code === 'ROUTE_BINDING_NOT_FOUND') {
        return t('routing_dr.events.error.target_gone');
      }
      if (error.code === 'ROUTING_PREFLIGHT_TOKEN_STALE') {
        return t('tenant_routing.configs.error.preflight_stale');
      }
      if (error.code === 'INVALID_PAYLOAD') {
        return reason.length > MAX_SWITCH_REASON_LENGTH
          ? t('tenant_routing.configs.error.reason_too_long')
          : t('tenant_routing.configs.error.reason_required');
      }
    }
    return coerceErrorMessage(error);
  }

  async function handleRollbackSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (draft === null || draft.needs_reconfirm || draft.reread_failed || syncingHead) return;
    const reason = draft.reason.trim();
    if (reason.length === 0) {
      toast.error(t('tenant_routing.configs.error.reason_required'));
      return;
    }
    try {
      const response = await rollback.mutateAsync({
        reason,
        expected_event_id: draft.event_id,
      });
      setDraft(null);
      // An engaged override sits above the global config in the ladder, so a
      // global switch that committed and converged perfectly is still not what
      // any tenant is being served. That outranks every convergence outcome
      // below: convergence describes whether the fleet can *see* the write,
      // and this describes whether the write has any effect at all.
      if (response.override_in_force === true) {
        toast.info(t('routing_dr.events.toast.rolled_back_override_in_force'));
        return;
      }
      const outcome = convergenceOutcome(response.cache);
      if (outcome === 'degraded') {
        toast.info(t('routing_dr.events.toast.rolled_back_degraded', {
          // Both instants come off the gateway clock; see the helper for why
          // the browser's is not in this subtraction.
          seconds: convergenceWindowSeconds(
            response.cache,
            response.event.switched_at,
            Date.now(),
          ),
        }));
      } else if (outcome === 'unknown') {
        // The rollback landed; the gateway said nothing about propagation.
        // Reporting that as a plain success would invite exactly the "read
        // traffic immediately" conclusion the degraded copy exists to prevent.
        toast.info(t('routing_dr.events.toast.rolled_back_unknown', {
          seconds: ROUTING_CONVERGENCE_WORST_CASE_SECONDS,
        }));
      } else {
        toast.success(t('routing_dr.events.toast.rolled_back'));
      }
    } catch (error) {
      if (error instanceof OpsApiError && error.code === 'ROUTING_SWITCH_HEAD_MOVED') {
        // Force the re-confirmation: the refusal is authority enough, and a
        // re-read that cannot see the other write must not clear the banner.
        await syncHeadIntoDraft(reason, true);
      }
      toast.error(rollbackErrorMessage(error, reason));
    }
  }

  return (
    <>
      {/*
        Rollback lives in this panel, and a rollback is a global switch — so it
        is a no-op while the override is engaged. The warning belongs where the
        button is, not only on the override's own panel.
      */}
      <div className="mb-3 empty:hidden">
        <OverrideInForceNotice />
      </div>
      <Panel
        caption={t('routing_dr.events.title')}
        testId="routing-dr-events-panel"
        actions={
          <>
            {/*
              The log is the latest N, not the whole history. Stated in the
              caption rather than only in the empty state, which an operator
              reading a populated table never sees.
            */}
            <span className="text-micro text-paper-low" data-testid="routing-dr-events-bound">
              {t('routing_dr.events.bound', { count: SWITCH_EVENTS_PAGE_SIZE })}
            </span>
            {rollbackTarget === null ? null : (
              <Button
                size="sm"
                onClick={() => openRollback(rollbackTarget)}
                data-testid="routing-dr-rollback-open"
              >
                {t('routing_dr.events.rollback_to', {
                  config: rollbackTarget.from_config_id ?? '',
                })}
              </Button>
            )}
          </>
        }
      >
        {events.isPending ? (
          <p className="m-0 p-4 text-body text-paper-low" data-testid="routing-dr-events-loading">
            {t('routing_dr.events.loading')}
          </p>
        ) : rows.length === 0 ? (
          <EmptyState
            title={t('routing_dr.events.empty_title')}
            hint={t('routing_dr.events.empty_hint')}
            testId="routing-dr-events-empty"
          />
        ) : (
          <Table>
            <THead>
              <Tr interactive={false}>
                <Th>{t('routing_dr.events.column.when')}</Th>
                <Th>{t('routing_dr.events.column.change')}</Th>
                <Th>{t('routing_dr.events.column.actor')}</Th>
                <Th>{t('routing_dr.events.column.reason')}</Th>
                <Th>{t('routing_dr.events.column.propagation')}</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((event) => (
                <Tr key={event.id} interactive={false} data-testid={`routing-dr-event-row-${event.id}`}>
                  <Td mono className="whitespace-nowrap">
                    {formatLocalTimestamp(event.switched_at, { seconds: true })}
                  </Td>
                  <Td mono>
                    {event.from_config_id ?? t('routing_dr.events.no_previous')}
                    {' → '}
                    {event.to_config_id ?? '—'}
                    {event.preflight === null ? null : (
                      <span className="mt-0.5 block text-micro text-paper-low">
                        {t('routing_dr.events.preflighted')}
                      </span>
                    )}
                  </Td>
                  <Td mono>{event.actor_user_id ?? '—'}</Td>
                  <Td>{event.reason}</Td>
                  <Td>
                    <EventConvergenceCell event={event} />
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
        )}
      </Panel>

      <Dialog
        open={draft !== null}
        onOpenChange={(open) => {
          if (!open) setDraft(null);
        }}
        title={t('routing_dr.events.rollback_title')}
        closeLabel={t('common.close')}
        testId="routing-dr-rollback-dialog"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)} disabled={rollback.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              type="submit"
              form="routing-dr-rollback-form"
              loading={rollback.isPending}
              disabled={
                syncingHead
                || draft?.needs_reconfirm === true
                || draft?.reread_failed === true
              }
              data-testid="routing-dr-rollback-save"
            >
              {t('routing_dr.events.rollback_save')}
            </Button>
          </>
        }
      >
        {draft === null ? null : (
          <form
            id="routing-dr-rollback-form"
            className="flex flex-col gap-4"
            onSubmit={handleRollbackSubmit}
            data-testid="routing-dr-rollback-form"
          >
            {/*
              States what the endpoint actually does — reverse the latest
              switch — and names that switch in full, so "roll back to A" can
              never be read as a free choice of target.
            */}
            <div
              className="flex flex-col gap-1 rounded-md bg-well px-3 py-2 text-dense text-paper-low"
              data-testid="routing-dr-rollback-target"
            >
              <span>{t('routing_dr.events.rollback_explainer')}</span>
              <span className="font-mono text-paper-mid">
                {draft.to_config_id} → {draft.from_config_id}
              </span>
            </div>
            {syncingHead ? (
              <p className="m-0 text-dense text-paper-low" data-testid="routing-dr-rollback-syncing">
                {t('routing_dr.events.rollback_syncing')}
              </p>
            ) : null}
            {draft.reread_failed && !syncingHead ? (
              <div
                className="flex flex-col items-start gap-2 rounded-md border border-bad-line bg-bad-dim/50 px-3 py-2"
                role="alert"
                data-testid="routing-dr-rollback-reread-failed"
              >
                <p className="m-0 text-dense leading-5 text-bad-bright">
                  {t('routing_dr.events.rollback_reread_failed')}
                </p>
                <Button
                  size="sm"
                  onClick={() => void syncHeadIntoDraft(draft.reason)}
                  data-testid="routing-dr-rollback-retry-reread"
                >
                  {t('routing_dr.events.rollback_retry_reread')}
                </Button>
              </div>
            ) : null}
            {draft.needs_reconfirm ? (
              <div
                className="flex flex-col items-start gap-2 rounded-md border border-warn-line bg-warn-dim/50 px-3 py-2"
                role="alert"
                data-testid="routing-dr-rollback-head-changed"
              >
                <p className="m-0 text-dense leading-5 text-warn">
                  {t('routing_dr.events.rollback_head_changed')}
                </p>
                <Button
                  size="sm"
                  onClick={() => setDraft((current) =>
                    current === null ? current : { ...current, needs_reconfirm: false })}
                  data-testid="routing-dr-rollback-reconfirm"
                >
                  {t('routing_dr.events.rollback_reconfirm')}
                </Button>
              </div>
            ) : null}
            <FormField
              label={t('tenant_routing.configs.switch_reason')}
              fieldId="routing-dr-rollback-reason"
              required
              hint={t('tenant_routing.configs.switch_reason_hint')}
            >
              <Textarea
                id="routing-dr-rollback-reason"
                rows={3}
                maxLength={MAX_SWITCH_REASON_LENGTH}
                value={draft.reason}
                onChange={(event) => {
                  const reason = event.currentTarget.value;
                  setDraft((current) => current === null ? current : { ...current, reason });
                }}
                placeholder={t('routing_dr.events.rollback_reason_placeholder')}
                data-testid="routing-dr-rollback-reason"
              />
            </FormField>
            <p className="m-0 text-dense leading-5 text-paper-low" data-testid="routing-dr-rollback-propagation">
              {t('routing_dr.propagation.hint', {
                seconds: ROUTING_CONVERGENCE_WORST_CASE_SECONDS,
              })}
            </p>
          </form>
        )}
      </Dialog>
    </>
  );
}

function EventConvergenceCell({
  event,
}: {
  readonly event: RoutingSwitchEventRecord;
}): JSX.Element {
  const t = useT();
  const outcome = eventConvergenceOutcome(event.cache_convergence);
  const tone = outcome === 'immediate' ? 'ok' : outcome === 'degraded' ? 'warn' : 'neutral';
  return (
    <span className="flex flex-col gap-0.5">
      <Chip tone={tone} testId={`routing-dr-event-convergence-${event.id}`}>
        {t(`routing_dr.convergence.${outcome}`)}
      </Chip>
      {event.cache_convergence !== null && event.cache_convergence.status === 'recorded' ? (
        <span className="font-mono text-micro text-paper-low">
          {formatLocalTimestamp(event.cache_convergence.effective_after, { seconds: true })}
        </span>
      ) : null}
    </span>
  );
}
